import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';

import {ServerResponseHandler} from 'src/components/server-response-handler';
import {MessageContext} from 'src/contexts/message-context';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {
    REMOVE_GRANT_MODAL_WIDTH,
    RemoveGrantModal,
} from 'src/modules/manage-entitlements/edit-grant/remove-grant-modal';
import {getGrantsWithoutRelations} from 'src/openapi/admin/apis/EntitlementsGrantApi';
import {useAppDispatch} from 'src/store/hooks';

import {BoxLoading} from '@pb/kit-component-box-loading';
import {Dialog} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';
import {PaginationStripe} from '@pb/kit-component-pagination';
import {Body, Cell, Head, Table} from '@pb/kit-component-table';


import {replace} from '../../entitlement-drawer.slice';

import {NotAssociatedGrantRow} from './not-associated-grant-row';

import type {OpenRequest} from 'src/modules/entitlements/components/drawer/typedef';
import type {Grant} from 'src/openapi/admin/models/Grant';
import type {GrantList} from 'src/openapi/admin/models/GrantList';
import type {Pagination} from 'src/openapi/admin/models/Pagination';
import type {ServerResponse} from 'src/typedef';

type Props = {
    currentEntitlement: OpenRequest,
};

export const NotAssociatedGrantsTable = ({currentEntitlement}: Props) => {
    const dispatch = useDispatch();
    const apiDispatch = useAppDispatch();
    const {setSuccessMessage} = useContext(MessageContext);

    const pagination = useMemo(() => (
        currentEntitlement?.notAssociatedEntitlementsDetails?.pagination || {currentPage: 1, pageSize: 10}
    ), [currentEntitlement]);

    const [grantForDeletion, setGrantForDeletion] = useState<Grant | null>(null);

    const [response, setResponse] = useState<ServerResponse<GrantList> | null>(null);

    const loadNotAssociatedGrants = useCallback(async () => {
        setResponse(null);
        const serverResponse = await apiDispatch(getGrantsWithoutRelations({
            pageNumber: pagination.currentPage,
            pageSize: pagination.pageSize,
        }));

        if (serverResponse.payload.success) {
            setResponse({data: serverResponse.payload.data});
        } else {
            setResponse({error: serverResponse.payload.data});
        }
    }, [pagination, setResponse, apiDispatch]);

    useEffect(() => {
        loadNotAssociatedGrants();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffectWithDeps((prevDeps, currentDeps) => {
        const {pagination: prevPagination} = prevDeps;
        const {
            loadNotAssociatedGrants: currentLoad,
            pagination: currentPagination,
        } = currentDeps;

        const paginationChanged =
            prevPagination.currentPage !== currentPagination.currentPage ||
            prevPagination.pageSize !== currentPagination.pageSize;

        if (paginationChanged) {
            currentLoad();
        }
    }, {loadNotAssociatedGrants, pagination});

    const onChangePageSize = useCallback((_currentPage: number, pageSize: number) => {
        if (currentEntitlement?.notAssociatedEntitlementsDetails) {
            dispatch(replace({
                ...currentEntitlement,
                notAssociatedEntitlementsDetails: {
                    ...currentEntitlement.notAssociatedEntitlementsDetails,
                    pagination: {currentPage: 1, pageSize},
                },
            }));
        }
    }, [dispatch, currentEntitlement]);

    const closeDeleteGrantModal = useCallback(() => {
        setGrantForDeletion(null);
    }, [setGrantForDeletion]);

    const onRemove = useCallback(() => {
        if (response?.data?.totalCount) {
            const totalPages = Math.ceil((response.data.totalCount - 1) / pagination.pageSize);

            if ((response.data.totalCount - 1) && pagination.currentPage > totalPages) {
                if (currentEntitlement?.notAssociatedEntitlementsDetails) {
                    dispatch(replace({
                        ...currentEntitlement,
                        notAssociatedEntitlementsDetails: {
                            ...currentEntitlement.notAssociatedEntitlementsDetails,
                            pagination: {currentPage: totalPages, pageSize: pagination.pageSize},
                        },
                    }));
                }
            } else {
                loadNotAssociatedGrants();
            }
        }

        setGrantForDeletion(null);
        setSuccessMessage('Grant removed successfully');
    }, [
        response,
        pagination,
        setGrantForDeletion,
        setSuccessMessage,
        dispatch,
        loadNotAssociatedGrants,
        currentEntitlement,
    ]);

    const onPaginationChange = useCallback((newPagination: Pagination) => {
        if (currentEntitlement?.notAssociatedEntitlementsDetails) {
            dispatch(replace({
                ...currentEntitlement,
                notAssociatedEntitlementsDetails: {
                    ...currentEntitlement.notAssociatedEntitlementsDetails,
                    pagination: newPagination,
                },
            }));
        }
    }, [dispatch, currentEntitlement]);

    return (
        <ServerResponseHandler serverError={response?.error || null} smallView={true}>
            <Group direction="v" alignItems="start">
                <b>Grants not associated with any resources or not assigned to any user or account</b>
                <BoxLoading loading={!response}>
                    <Group direction="v" alignItems="start">
                        <Table borderedGrid={true}>
                            <Head>
                                <Cell width={56}><strong>#</strong></Cell>
                                <Cell>Grant ({response?.data?.totalCount})</Cell>
                                <Cell width={50}/>
                            </Head>
                            <Body>
                                {response?.data?.items?.map((grant, index) => (
                                    <NotAssociatedGrantRow
                                        key={grant.id}
                                        grant={grant}
                                        index={index}
                                        pagination={pagination}
                                        setGrantForDeletion={setGrantForDeletion}
                                    />
                                ))}
                            </Body>
                        </Table>
                        <PaginationStripe
                            pageSize={pagination.pageSize}
                            totalCount={response?.data?.totalCount || 0}
                            currentPage={pagination.currentPage}
                            onPaginationChange={onPaginationChange}
                            onChangePaginationSize={onChangePageSize}
                            sizeSwitcherPosition="LEFT"
                            sizeSwitcherType="ONLY_PAGE_SIZE"
                            sizes={[5, 10]}
                        />
                    </Group>
                </BoxLoading>
                <Dialog
                    shown={!!grantForDeletion}
                    setShown={closeDeleteGrantModal}
                    wrapperProps={{style: {width: REMOVE_GRANT_MODAL_WIDTH}}}>
                    {grantForDeletion ? (
                        <RemoveGrantModal
                            grantId={grantForDeletion.id}
                            grantName={grantForDeletion.name}
                            onClose={closeDeleteGrantModal}
                            onDone={onRemove}
                        />
                    ) : null}
                </Dialog>
            </Group>
        </ServerResponseHandler>
    );
};
